import { readPlayerCharacterField as pcField } from '../display/player-character-field';

// ExperienceTier cumulative marks for levels 0..40, shared by the sheet and GM award action.
const TOTAL_MARKS = [0, 1, 2, 3, 4, 5, 6, 7, 9, 11, 13, 15, 18, 21, 24, 27, 31, 35, 39, 43, 48,
    53, 58, 63, 69, 75, 81, 87, 94, 101, 108, 115, 123, 131, 139, 147, 156, 165, 174, 183, 193] as const;

export function canLevelUp(level: number, marks: number): boolean {
    const next = TOTAL_MARKS.find((_marks, index) => index > level);
    return next !== undefined && marks >= next;
}

export function normalizeExperienceMarks(value: unknown, minimum = 0): number {
    if (!(typeof value === 'number' || typeof value === 'string' && /^\d+(?:\.\d+)?$/.test(value.trim())))
        throw new Error('Experience Marks must be non-negative and use increments of 0.5.');
    const number = Number(value);
    if (!Number.isFinite(number) || number < 0 || number > 2147483647 || !Number.isInteger(number * 2))
        throw new Error('Experience Marks must be non-negative and use increments of 0.5.');
    return Math.max(number, minimum);
}

export function minimumExperienceMarks(level: number): number { return TOTAL_MARKS[level] ?? 0; }

export interface ExperienceAward {
    item_id: string;
    before: number;
    after: number;
    log_type: 'can_level_up' | 'awarded_experience_marks';
    roll_result: number;
}

/** Saved PC columns are sufficient: level and the award/log choice are browser decisions. */
export function planExperienceAward(players: readonly unknown[], rawAmount: unknown): ExperienceAward[] {
    const amount = normalizeExperienceMarks(rawAmount);
    if (amount <= 0) throw new Error('Experience Marks must be greater than zero.');
    if (amount >= 200) throw new Error('Experience Marks must be less than 200.');
    return players.filter(player => Number(pcField(player, 'player_character_inactive') ?? 0) !== 1).map(player => {
        const before = normalizeExperienceMarks(pcField(player, 'experience_marks') ?? 0), after = normalizeExperienceMarks(before + amount);
        let level = 0;
        for (let index = 1; index <= 20; index++) level += Number(pcField(player, `class_level_${index}`) ?? 0);
        const levelUp = canLevelUp(level, after);
        return { item_id: String(pcField(player, 'item_id') ?? ''), before, after,
            log_type: levelUp ? 'can_level_up' : 'awarded_experience_marks', roll_result: levelUp ? 0 : amount };
    });
}
